import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faSpinner, faPlus, faTrash, faExternalLinkAlt, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDateTime } from '../../utils/http';

interface Webinar {
  id: string;
  title: string;
  description: string;
  platform: string;
  joinUrl: string;
  scheduledAt: string;
  durationMinutes: number;
  recordingUrl: string | null;
}

interface Props {
  learnershipId: string;
  canManage: boolean;
}

const PLATFORMS = ['Microsoft Teams', 'Zoom', 'Google Meet', 'Skype', 'Other'];

export default function WebinarsPanel({ learnershipId, canManage }: Props) {
  const { accessToken } = useAuth();
  const [items, setItems] = useState<Webinar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const [joinUrl, setJoinUrl] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState('60');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await authFetch<Webinar[]>(accessToken, `/api/community/courses/${learnershipId}/webinars`);
    if (res.ok && res.data) {
      setItems(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load sessions.');
    }
    setIsLoading(false);
  }, [accessToken, learnershipId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setError('');
    if (!title.trim() || !joinUrl.trim() || !scheduledAt) {
      setError('Title, join link and date/time are required.');
      return;
    }
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/community/courses/${learnershipId}/webinars`, {
      method: 'POST',
      body: {
        title,
        description,
        platform,
        joinUrl,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes: parseInt(duration, 10) || 60,
      },
    });
    setIsSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setTitle('');
    setDescription('');
    setJoinUrl('');
    setScheduledAt('');
    setShowForm(false);
    load();
  };

  const addRecording = async (id: string) => {
    const url = window.prompt('Paste the recording link (https://...)');
    if (url === null) return;
    const res = await authFetch(accessToken, `/api/community/webinars/${id}/recording`, {
      method: 'PUT',
      body: { recordingUrl: url },
    });
    if (!res.ok) setError(res.message);
    else load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this session?')) return;
    const res = await authFetch(accessToken, `/api/community/webinars/${id}`, { method: 'DELETE' });
    if (!res.ok) setError(res.message);
    else load();
  };

  const now = Date.now();

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm((v) => !v)}
            className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            Schedule session
          </button>
        </div>
      )}

      {showForm && canManage && (
        <div className="card-surface p-5 space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Session title"
            className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What will be covered?"
            className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {PLATFORMS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
            <input
              type="url"
              value={joinUrl}
              onChange={(e) => setJoinUrl(e.target.value)}
              placeholder="Join link (https://...)"
              className="px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="Duration (minutes)"
              className="px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={create}
              disabled={isSaving}
              className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
              Schedule
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <FontAwesomeIcon icon={faVideo} className="h-12 w-12 text-brand-300 mb-4" />
          <p className="text-brand-900/60">No live sessions scheduled.</p>
        </div>
      ) : (
        items.map((w) => {
          const end = new Date(w.scheduledAt).getTime() + w.durationMinutes * 60000;
          const isPast = end < now;
          return (
            <div key={w.id} className="card-surface p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="text-base font-bold text-brand-950">{w.title}</h4>
                  <p className="text-xs text-brand-900/50 mt-0.5">
                    {w.platform} · {formatDateTime(w.scheduledAt)} · {w.durationMinutes} min
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${isPast ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}`}>
                  {isPast ? 'Ended' : 'Upcoming'}
                </span>
              </div>
              {w.description && <p className="text-sm text-brand-900/80 mt-3 whitespace-pre-wrap">{w.description}</p>}
              <div className="flex flex-wrap gap-2 mt-4">
                {!isPast && (
                  <a
                    href={w.joinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="h-3 w-3" />
                    Join session
                  </a>
                )}
                {w.recordingUrl && (
                  <a
                    href={w.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faPlayCircle} className="h-3 w-3" />
                    Watch recording
                  </a>
                )}
                {canManage && (
                  <>
                    <button
                      onClick={() => addRecording(w.id)}
                      className="px-4 py-2 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors"
                    >
                      {w.recordingUrl ? 'Change recording link' : 'Add recording link'}
                    </button>
                    <button onClick={() => remove(w.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                      <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
